"use client"

import '../mockups/_styles/editorial.css'
import { ArticlePreviewCard } from '@/components/brand-kit/article-preview-card'
import type { ArticlePreview } from '@/lib/article-previews'

import { useSearchParams } from "next/navigation"
import { useState, useEffect, Suspense } from "react"
import Link from "next/link"
import { SiteHeader } from "@/components/site-header"
import { SiteFooter } from "@/components/site-footer"
import { Search, Loader2, X } from "lucide-react"
import { formatProductTitle } from "@/lib/breadcrumb-utils"
import { ProductGridCard } from "@/components/brand-kit/product-grid-card"
import { badgesForSignals } from "@/components/badges/from-product"
import type { Badge } from "@/components/badges/config"
import { TrustChips } from "@/components/price-intelligence/TrustChips"
import { formatCents } from "@/lib/format-utils"
import { loadSearchResponse } from '@/lib/search/request'
import { useSearchOutcomes } from '@/hooks/use-search-outcomes'
import { SITE_STATS } from "@/lib/site-stats"

// Curated top collections — every slug verified live (07/20/2026).
const SEARCH_TOP_COLLECTIONS: Array<{ slug: string; label: string }> = [
  { slug: 'refrigerators', label: 'Refrigerators' },
  { slug: 'french-door-refrigerators', label: 'French Door' },
  { slug: 'mini-fridges', label: 'Mini Fridges' },
  { slug: 'freezers', label: 'Freezers' },
  { slug: 'wine-coolers', label: 'Wine Coolers' },
  { slug: 'ice-makers', label: 'Ice Makers' },
]

interface SearchProduct {
  id: string
  title: string
  brand: string
  model: string
  type: string
  url: string
  image: string | null
  images: string[]
  price: number | null
  originalPrice: number | null
  rating: number
  reviews: number
  retailerName: string | null
  offerId: string | null
  created_at?: string
  specs_json?: {
    rating?: number
    reviews?: number
    energy_star?: boolean
  }
  availabilityMaxQty?: number | null
  dealBadge?: string | null
  /** Canonical server-computed badge set (spec §6) — emitted by /api/search. */
  badges?: Badge[]
}

/** Non-product hit shapes the API returns alongside products in page mode. */
interface SearchLink {
  preview?: ArticlePreview
  title: string
  url: string
  type: string
  image: string | null
}

/** A constraint the API read out of the query (FRIDGE-811) — shown as a removable chip. */
interface SearchConstraint {
  kind: string
  label: string
  source: string
  /** The query with this constraint's words removed — the chip's × goes here. */
  removeQuery: string
}

/** Set when the exact constraints matched nothing and a relaxed set is shown. */
interface RelaxNote {
  note: string
  understood: string
}

/**
 * What the search understood, as chips the reader can take off one at a time,
 * plus the plain-English note when the exact set had to be relaxed.
 */
function ConstraintChips({ constraints, relaxed }: { constraints: SearchConstraint[]; relaxed: RelaxNote | null }) {
  if (constraints.length === 0) return null
  return (
    <div className="mb-6" data-testid="search-constraints">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Filtering by</span>
        {constraints.map((c) => (
          <span
            key={`${c.kind}-${c.source}`}
            data-constraint={c.kind}
            className="inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-full border border-[#0469f0]/30 bg-[#f2f7ff] text-sm text-[#0b3d91]"
          >
            {c.label}
            <Link
              href={c.removeQuery ? `/search?q=${encodeURIComponent(c.removeQuery)}` : '/search'}
              aria-label={`Remove ${c.label}`}
              title={`Remove ${c.label}`}
              className="inline-flex items-center justify-center h-6 w-6 rounded-full text-[#0b3d91]/70 hover:bg-[#0469f0]/10 hover:text-[#0469f0] transition-colors"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </span>
        ))}
      </div>
      {relaxed ? (
        <p className="mt-3 text-sm text-gray-700" role="status" data-testid="search-relaxed">
          {relaxed.note}
        </p>
      ) : null}
    </div>
  )
}

function SearchResults() {
  const searchParams = useSearchParams()
  const query = searchParams.get("q") || ""
  const [products, setProducts] = useState<SearchProduct[]>([])
  const [brands, setBrands] = useState<SearchLink[]>([])
  const [collections, setCollections] = useState<SearchLink[]>([])
  const [tools, setTools] = useState<SearchLink[]>([])
  const [articles, setArticles] = useState<SearchLink[]>([])
  const [loading, setLoading] = useState(true)
  const [searchFailed, setSearchFailed] = useState(false)
  const { recordOutcome, recordSelection } = useSearchOutcomes()
  const [totalCount, setTotalCount] = useState(0)
  const [constraints, setConstraints] = useState<SearchConstraint[]>([])
  const [relaxed, setRelaxed] = useState<RelaxNote | null>(null)
  const [mobile, setMobile] = useState(false)

  useEffect(() => {
    const media = window.matchMedia('(max-width: 767px)')
    const update = () => setMobile(media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    const clearResults = () => {
      setProducts([])
      setBrands([])
      setCollections([])
      setTools([])
      setArticles([])
      setTotalCount(0)
      setConstraints([])
      setRelaxed(null)
    }
    const fetchResults = async () => {
      if (!query || query.length < 2) {
        clearResults()
        setSearchFailed(false)
        setLoading(false)
        return
      }

      setLoading(true)
      setSearchFailed(false)
      try {
        const data = await loadSearchResponse<{
          products?: SearchProduct[]; brands?: SearchLink[]; collections?: SearchLink[];
          tools?: SearchLink[]; articles?: SearchLink[]; totalCount?: number;
          constraints?: SearchConstraint[]; relaxed?: RelaxNote;
        }>(query, 100, controller.signal)
        if (!data || controller.signal.aborted) return
        recordOutcome(query, data.totalCount || 0, data.constraints)
        // Full search mode (limit > 10) returns 'products', not 'results' —
        // plus the brand/collection/tool/article hits the typeahead shows
        // (FRIDGE-436: the page used to throw these away).
        setProducts(data.products || [])
        setBrands(data.brands || [])
        setCollections(data.collections || [])
        setTools(data.tools || [])
        setArticles(data.articles || [])
        setTotalCount(data.totalCount || 0)
        setConstraints(Array.isArray(data.constraints) ? data.constraints : [])
        setRelaxed(data.relaxed && typeof data.relaxed.note === 'string' ? data.relaxed : null)
      } catch {
        if (controller.signal.aborted) return
        clearResults()
        setSearchFailed(true)
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }

    fetchResults()
    return () => controller.abort()
  }, [query, recordOutcome])

  if (!query || query.length < 2) {
    return (
      <div className="text-center py-16">
        <Search className="mx-auto h-16 w-16 text-gray-300 mb-4" />
        <h2 className="text-xl font-medium text-gray-600">Enter a search term</h2>
        <p className="text-gray-500 mt-2">Type at least 2 characters to search</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-[#0469f0]" />
        <span className="ml-3 text-gray-600">Searching for &quot;{query}&quot;...</span>
      </div>
    )
  }

  if (searchFailed) {
    return (
      <div className="text-center py-16" role="alert">
        <h2 className="text-xl font-medium text-gray-600">Search is unavailable</h2>
        <p className="text-gray-500 mt-2">Please retry your search. We could not load the results.</p>
      </div>
    )
  }

  if (products.length === 0 && brands.length === 0 && collections.length === 0 && tools.length === 0 && articles.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="flex justify-center">
          <ConstraintChips constraints={constraints} relaxed={relaxed} />
        </div>
        <Search className="mx-auto h-16 w-16 text-gray-300 mb-4" />
        <h2 className="text-xl font-medium text-gray-600">No results found</h2>
        <p className="text-gray-500 mt-2">
          {relaxed ? relaxed.note : <>We couldn&apos;t find any products matching &quot;{query}&quot;</>}
        </p>
        <p className="text-gray-500 mt-4 text-sm">
          Try different keywords, or start from a popular collection:
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {SEARCH_TOP_COLLECTIONS.map((c) => (
            <Link
              key={c.slug}
              href={`/collections/${c.slug}`}
              className="px-4 py-2 rounded-full border border-gray-200 bg-white text-sm text-gray-700 hover:border-[#0469f0]/40 hover:text-[#0469f0] transition-colors"
            >
              {c.label}
            </Link>
          ))}
        </div>
        <div className="mt-8 flex justify-center">
          <TrustChips />
        </div>
      </div>
    )
  }

  const suggestions = ((brands.length > 0 || collections.length > 0) && (
        <div className="mb-6 flex flex-wrap gap-2" data-testid="search-suggestions">
          {brands.map((b) => (
            <Link
              key={b.url}
              href={b.url}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gray-200 bg-white text-sm text-gray-800 hover:border-[#0469f0]/40 hover:text-[#0469f0] transition-colors"
            >
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[#0469f0]">Brand</span>
              {b.title}
            </Link>
          ))}
          {collections.map((c) => (
            <Link
              key={c.url}
              href={c.url}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gray-200 bg-white text-sm text-gray-800 hover:border-[#0469f0]/40 hover:text-[#0469f0] transition-colors"
            >
              <span className="text-[11px] font-semibold uppercase tracking-wide text-orange-500">Collection</span>
              {c.title}
            </Link>
          ))}
        </div>
      ))

  return (
    <div onClickCapture={(event) => {
      const target = event.target instanceof Element ? event.target.closest('a[href]') : null
      const destination = target?.getAttribute('href')
      const isResult = destination && [...products, ...brands, ...collections, ...tools, ...articles].some(item => item.url === destination)
      if (isResult) recordSelection(query, totalCount, destination, constraints)
    }}>
      {/* What the search understood — removable chips, and the relax note when
          the exact set found nothing (FRIDGE-811). */}
      <ConstraintChips constraints={constraints} relaxed={relaxed} />

      {/* One interactive copy; DOM order follows the viewport (FRIDGE-1000). */}
      {!mobile && suggestions}

      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <p className="text-gray-600">
          Found <span className="font-semibold">{totalCount}</span> {totalCount === 1 ? 'result' : 'results'} for &quot;{query}&quot;
          {totalCount > products.length && products.length > 0 && (
            <span className="text-gray-500"> — showing the first {products.length}</span>
          )}
        </p>
        <TrustChips variant="compact" />
      </div>

      {products.length === 0 && (
        <p className="mb-6 text-sm text-gray-600" role="status">No product matches for &quot;{query}&quot;. Explore the other matches on this page.</p>
      )}
      <div data-testid="search-products" className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {products.map((item, index) => {
          const priceValue = item.price || 0
          const originalPriceValue = item.originalPrice || 0
          const price = priceValue > 0 ? formatCents(priceValue) : undefined
          const originalPrice = originalPriceValue > priceValue && originalPriceValue > 0
            ? formatCents(originalPriceValue)
            : undefined
          const images = item.images?.length > 0
            ? item.images
            : (item.image ? [item.image] : [])

          // No skip for image-less results: ProductGridCard renders its own
          // NoImageFallback for images=[] (FRIDGE-436 defect 1 — dropping
          // them made the rendered count disagree with the stated count).

          // Canonical badges — SERVER-computed by /api/search via resolveBadges
          // (spec §6): the same array the product shows on every other surface,
          // Real-or-Hidden (empty array → BadgeList renders nothing). The
          // client-side engine remains only as a fallback for cached payloads
          // that predate the `badges` field.
          const badges = Array.isArray(item.badges)
            ? item.badges
            : badgesForSignals({
                priceCents: item.price ?? undefined,
                originalPriceCents: item.originalPrice ?? undefined,
                rating: item.specs_json?.rating,
                reviews: item.specs_json?.reviews,
                energyStar: item.specs_json?.energy_star === true,
                createdAt: item.created_at,
                availabilityMaxQty: item.availabilityMaxQty ?? undefined,
                dealBadge: item.dealBadge ?? undefined,
              })

          return (
            <ProductGridCard
              key={item.id}
              images={images}
              brand={item.brand || undefined}
              title={formatProductTitle(item.title)}
              price={price}
              originalPrice={originalPrice}
              rating={item.rating > 0 ? item.rating : undefined}
              href={item.url}
              badges={badges}
              model={item.model || null}
              // FRIDGE-1506 D3: the first row (two cards on a phone) is in the
              // first screen, so its photos load eagerly; the release check of
              // 10/15/2026 found both lazy at 176 x 176 px on /search at 412 px.
              // FRIDGE-1546: the first asks for a high fetch priority.
              priority={index < 2}
              fetchPriority={index === 0 ? 'high' : undefined}
            />
          )
        })}
      </div>

      {mobile && suggestions ? <div className="mt-6">{suggestions}</div> : null}

      {/* Tools and guides — page mode gets the same KINDS the typeahead has
          (FRIDGE-436 defect 3), at page depth. */}
      {(tools.length > 0 || articles.length > 0) && (
        <div className="mt-10">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">
            Tools &amp; guides matching &quot;{query}&quot;
          </h2>
          <div className="flex flex-wrap gap-2">
            {tools.map((t) => (
              <Link
                key={t.url}
                href={t.url}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gray-200 bg-white text-sm text-gray-800 hover:border-[#0469f0]/40 hover:text-[#0469f0] transition-colors"
              >
                <span className="text-[11px] font-semibold uppercase tracking-wide text-[#0469f0]">Tool</span>
                {t.title}
              </Link>
            ))}

          </div>
          <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {articles.map(a => a.preview ? <ArticlePreviewCard key={a.url} article={a.preview} /> : null)}
          </div>
        </div>
      )}
    </div>
  )
}

function SearchHero() {
  const searchParams = useSearchParams()
  return <SearchHeroView query={searchParams.get("q") || ""} />
}

/**
 * FRIDGE-1504 L1504-18: the server HTML renders this same hero (query "") as
 * the Suspense fallback, eyebrow and subtitle included, so the swap to the
 * live hero moves nothing below it. The h1 holds two lines on phones because
 * "Results for “…”" wraps where "Search Fridge.com" does not.
 */
function SearchHeroView({ query }: { query: string }) {
  return (
    <section style={{ borderBottom: '1px solid #f3f4f6' }}>
      <div className="ed-container" style={{ paddingTop: 36, paddingBottom: 36 }}>
        <nav className="ed-breadcrumb" style={{ marginBottom: 24 }}>
          <Link href="/" prefetch={false}>Fridge.com</Link>
          <span className="sep" aria-hidden="true">/</span>
          <span className="leaf">Search</span>
        </nav>

        <div className="ed-eyebrow" style={{ marginBottom: 18 }}>
          <span className="dot" />
          {query ? `SEARCH RESULTS · "${query.toUpperCase()}"` : 'SEARCH FRIDGE.COM'}
        </div>

        <h1 className="ed-h1 max-sm:min-h-[2lh]" style={{ fontSize: 'clamp(40px, 5vw, 72px)' }}>
          {query ? (
            <>
              Results for{' '}
              <em style={{ fontStyle: 'italic', color: 'var(--fridge-orange)' }}>&ldquo;{query}&rdquo;</em>
            </>
          ) : (
            <>
              Search{' '}
              <em style={{ fontStyle: 'italic', color: 'var(--fridge-orange)' }}>Fridge.com</em>
            </>
          )}
        </h1>
        <p className="ed-subtitle" style={{ maxWidth: 720, fontSize: 17, marginTop: 12 }}>
          Search across {SITE_STATS.products} refrigerators, freezers, ice makers, and beverage centers — with regularly
          updated pricing, ENERGY STAR data, and customer ratings from the Fridge.com catalog.
        </p>
      </div>
    </section>
  )
}

export default function SearchPage() {
  return (
    <div className="ed-root min-h-screen flex flex-col bg-white">
      <SiteHeader />
      <main id="main-content" className="flex-1 ph-no-capture">
        <Suspense fallback={<SearchHeroView query="" />}>
          <SearchHero />
        </Suspense>
        <section>
          {/* FRIDGE-1504 L1504-18: the results area holds a full viewport from the
              first paint, so the footer starts below the fold and the results,
              the spinner or an empty state replace each other without moving it
              (CLS was 0.26-0.40 on this page). */}
          <div className="ed-container" style={{ paddingTop: 32, paddingBottom: 40, minHeight: '100svh' }}>
            <Suspense fallback={
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-[#0469f0]" />
              </div>
            }>
              <SearchResults />
            </Suspense>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  )
}
